import { useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext";
import ProtectedImage from "../components/ProtectedImage";

export default function Paintings() {
  const [paintings, setPaintings] = useState([]);
  const { dark } = useTheme();

  useEffect(() => {
    fetch("/api/paintings")
      .then((r) => r.json())
      .then(setPaintings)
      .catch(() => {});
  }, []);

  // Split into two staggered columns
  const left = paintings.filter((_, i) => i % 2 === 0);
  const right = paintings.filter((_, i) => i % 2 !== 0);

  return (
    <div
      className="min-h-screen transition-colors duration-300"
      style={{ background: dark ? "#1a1612" : "#fdf8f0" }}
    >
      <title>Paintings | 5 Minute Nursing School</title>
      <meta name="description" content="Acrylic paintings — mostly mountains and dramatic oceans — by Farjana Yasmin Mim, the antidote to a precise medical world." />
      <link rel="canonical" href="https://5minutenursingschool.com/paintings" />
      {/* Hero */}
      <header className="text-center pt-20 pb-10 px-4">
        <p
          className="text-xs font-semibold tracking-[0.3em] uppercase mb-4"
          style={{ color: dark ? "#a08060" : "#8b6340" }}
        >
          A Collection
        </p>
        <h1
          className="text-5xl md:text-6xl font-bold mb-3"
          style={{ fontFamily: "Georgia, 'Times New Roman', serif", color: dark ? "#e8dcc8" : "#3d2b1f" }}
        >
          Paintings &amp; Me
        </h1>
        <p className="text-lg" style={{ color: dark ? "#9a8070" : "#7a6050" }}>
          Watercolors, botanicals, and the occasional fever dream on paper.
        </p>
      </header>

      {/* Staggered 2-column gallery */}
      <main className="max-w-4xl mx-auto px-6 pb-24">
        {paintings.length === 0 && (
          <p
            className="text-center py-24 text-lg italic"
            style={{ color: dark ? "#6a5040" : "#c0a080" }}
          >
            Nothing framed yet — check back soon.
          </p>
        )}

        {paintings.length > 0 && (
          <div className="flex gap-6 items-start">
            {/* Left column */}
            <div className="flex-1 flex flex-col gap-6">
              {left.map((p) => (
                <PaintingCard key={p.id} painting={p} dark={dark} />
              ))}
            </div>
            {/* Right column — offset down for stagger */}
            <div className="flex-1 flex flex-col gap-6 mt-14">
              {right.map((p) => (
                <PaintingCard key={p.id} painting={p} dark={dark} />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function PaintingCard({ painting, dark }) {
  return (
    <figure
      className="rounded-2xl overflow-hidden"
      style={{
        background: dark ? "#2a201a" : "#fff8ee",
        border: `1px solid ${dark ? "#3d2b1f" : "#ddc8a8"}`,
        boxShadow: dark
          ? "0 12px 40px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.3)"
          : "0 12px 40px rgba(90,60,30,0.14), 0 2px 8px rgba(90,60,30,0.06)",
      }}
    >
      <div className="overflow-hidden">
        <ProtectedImage src={`/api/paintings/${painting.id}/image`} />
      </div>

      {painting.title && (
        <figcaption
          className="px-5 py-3 text-sm italic text-center"
          style={{
            fontFamily: "Georgia, serif",
            color: dark ? "#a08060" : "#7a5c40",
            borderTop: `1px solid ${dark ? "#3d2b1f" : "#e8d0b0"}`,
          }}
        >
          {painting.title}
        </figcaption>
      )}
    </figure>
  );
}
